import React from 'react';

import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { usePatchMembershipType } from 'src/features/membershipType/api/usePatchMembershipType';
import { LIST_RESTRICTED_GATEWAYS_RESOURCE } from 'src/features/membershipType/api/useListRestrictedGateways';
import RestrictedGatewayFormModal from 'src/features/membership/components/forms/restrictedGateway/RestrictedGatewayFormModal';
import { restrictedGatewayMessages } from 'src/features/membershipType/utils/messages';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalErrorMessages, generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useGetMembershipType } from 'src/features/membershipType/api/useGetMembershipType';
import { useParking } from 'src/providers/ParkingProvider';

interface CreateRestrictedGatewayFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    children?: React.ReactNode;
}

function CreateRestrictedGatewayFormModal<TData>({ className }: CreateRestrictedGatewayFormModalProps<TData>) {
    const { formatMessage } = useIntl();
    const { membershipTypeId = '' } = useParams();
    const { parkingId } = useParking();

    const { data: membershipTemplate } = useGetMembershipType({
        parkingId,
        membershipTypeId,
    });

    const { mutate: mutateRestrictedGateways, isPending } = usePatchMembershipType({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_RESTRICTED_GATEWAYS_RESOURCE] });
            toast({
                title: formatMessage(restrictedGatewayMessages.createRestrictedGatewaySuccess),
                description: formatMessage(restrictedGatewayMessages.createRestrictedGatewaySuccessDescription),
            });
        },
        onError: (error) => {
            if (error?.response?.status !== 400) return;
            const errorDetails = error.response?.data?.errors[0]?.detail;
            const errorCode = formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorDetails || formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    return (
        <RestrictedGatewayFormModal
            membershipTypeId={membershipTypeId}
            membershipTypeRestrictedGateways={membershipTemplate?.restricted_gateways}
            className={className}
            mutate={(values, onSuccess, onError) =>
                mutateRestrictedGateways({ parkingId, id: membershipTypeId, ...values }, { onSuccess, onError })
            }
            title={formatMessage(generalMessages.createEntry, {
                entry: formatMessage(restrictedGatewayMessages.gatewayPoint).toLowerCase(),
            })}
            icon={ICON_MAP.ADD}
            isPending={isPending}
            subtitle={formatMessage(restrictedGatewayMessages.addRestrictedGatewayModalSubtitle)}
        />
    );
}

export default CreateRestrictedGatewayFormModal;
